<template>
  <div class="card-design">
    <div v-if="error" class="alert alert-warning py-2 small mb-3">{{ error }}</div>

    <!-- Layout ---------------------------------------------------------- -->
    <h6 class="design-heading">Layout</h6>
    <p class="design-hint">
      Thumbnails render one of your own cards, so you are choosing on real text.
    </p>

    <div v-if="loadingTemplates" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-secondary"></div>
    </div>
    <div v-else class="template-grid">
      <button
        v-for="tpl in templates"
        :key="tpl.id"
        type="button"
        class="template-tile"
        :class="{ selected: draft.builtin === tpl.id }"
        :title="tpl.description"
        @click="draft.builtin = tpl.id"
      >
        <div class="template-thumb" :class="tpl.orientation">
          <img v-if="thumbs[tpl.id]" :src="thumbs[tpl.id]" :alt="tpl.id" />
          <div v-else class="thumb-loading"></div>
        </div>
        <div class="template-name">{{ tpl.id }}</div>
        <div class="template-orientation">{{ tpl.orientation }}</div>
      </button>
    </div>

    <!-- Style ----------------------------------------------------------- -->
    <h6 class="design-heading mt-4">Style</h6>

    <div class="row g-3">
      <div class="col-6 col-lg-3">
        <label class="form-label small">Rules text size</label>
        <div class="d-flex align-items-center gap-2">
          <input type="range" class="form-range" min="6" max="14" step="0.5"
                 v-model.number="draft.style.bodySize" />
          <span class="value-chip">{{ draft.style.bodySize }}pt</span>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <label class="form-label small">Title size</label>
        <div class="d-flex align-items-center gap-2">
          <input type="range" class="form-range" min="8" max="20" step="0.5"
                 v-model.number="draft.style.titleSize" />
          <span class="value-chip">{{ draft.style.titleSize }}pt</span>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <label class="form-label small">Max rules lines</label>
        <div class="d-flex align-items-center gap-2">
          <input type="range" class="form-range" min="2" max="12" step="1"
                 v-model.number="draft.style.bodyMaxLines" />
          <span class="value-chip">{{ draft.style.bodyMaxLines }}</span>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <label class="form-label small">Font</label>
        <select class="form-select form-select-sm" v-model="draft.style.fontFamily">
          <option value="Helvetica">Helvetica</option>
          <option value="Times">Times</option>
          <option value="Courier">Courier</option>
        </select>
      </div>
    </div>

    <div class="row g-3 mt-1">
      <div class="col-6 col-lg-3" v-for="c in colorFields" :key="c.key">
        <label class="form-label small">{{ c.label }}</label>
        <div class="color-row">
          <input type="color" class="form-control form-control-color form-control-sm"
                 v-model="draft.style[c.key]" />
          <code class="small text-muted">{{ draft.style[c.key] }}</code>
        </div>
      </div>
    </div>

    <div class="row g-3 mt-1 align-items-end">
      <div class="col-12 col-lg-5">
        <label class="form-label small">Print mode</label>
        <div class="btn-group w-100" role="group">
          <button v-for="m in printModes" :key="m.id" type="button"
                  class="btn btn-sm"
                  :class="draft.style.printMode === m.id ? 'btn-primary' : 'btn-outline-secondary'"
                  :title="m.hint"
                  @click="draft.style.printMode = m.id">
            {{ m.label }}
          </button>
        </div>
        <div class="form-text small">{{ activePrintHint }}</div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="showFlavor"
                 v-model="draft.style.showFlavor" />
          <label class="form-check-label small" for="showFlavor">Show flavour text</label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="showRepeats"
                 v-model="draft.style.showRepeats" />
          <label class="form-check-label small" for="showRepeats">Show copy count</label>
        </div>
      </div>
    </div>

    <!-- Per-type colours ------------------------------------------------ -->
    <template v-if="cardTypes.length">
      <h6 class="design-heading mt-4">Colour by card type</h6>
      <p class="design-hint">Optional. Makes types tellable apart at a glance.</p>
      <div class="row g-2">
        <div class="col-6 col-lg-3" v-for="type in cardTypes" :key="type">
          <label class="form-label small text-truncate d-block">{{ type }}</label>
          <div class="color-row">
            <input type="color" class="form-control form-control-color form-control-sm"
                   :value="draft.style.typeColors[type] || draft.style.accent"
                   @input="setTypeColor(type, $event.target.value)" />
            <button v-if="draft.style.typeColors[type]" type="button"
                    class="btn btn-sm btn-link p-0 small"
                    @click="clearTypeColor(type)">reset</button>
          </div>
        </div>
      </div>
    </template>

    <!-- Actions --------------------------------------------------------- -->
    <div class="d-flex gap-2 align-items-center mt-4 pt-3 border-top">
      <button class="btn btn-primary" :disabled="saving || !dirty" @click="save">
        <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
        Save design
      </button>
      <button class="btn btn-outline-secondary" :disabled="!dirty" @click="reset">
        Discard changes
      </button>
      <span v-if="justSaved" class="text-success small ms-1">Saved — applies to every card.</span>
      <span v-else-if="dirty" class="text-muted small ms-1">Preview is live; nothing saved yet.</span>
    </div>
  </div>
</template>

<script>
import { sparkforgeApi, svgToDataUri } from '@/sparkforgeApi';

const COLOR_FIELDS = [
  { key: 'accent', label: 'Title bar' },
  { key: 'paper', label: 'Card background' },
  { key: 'ink', label: 'Text' },
  { key: 'border', label: 'Border' }
];

const PRINT_MODES = [
  { id: 'color', label: 'Colour', hint: 'As designed.' },
  { id: 'ink-saver', label: 'Ink saver', hint: 'White card, outlined title bar — around 75% less toner on a home printer.' },
  { id: 'mono', label: 'Mono', hint: 'Pure black on white, for laser printing and photocopying.' }
];

export default {
  name: 'CardDesignPanel',
  props: {
    gameId: { type: String, required: true },
    // A representative card, so thumbnails show real text rather than lorem.
    sampleCard: { type: Object, default: () => ({}) },
    cardTypes: { type: Array, default: () => [] }
  },
  emits: ['change', 'saved'],
  data() {
    return {
      templates: [],
      thumbs: {},
      loadingTemplates: true,
      saving: false,
      justSaved: false,
      error: null,
      saved: null,
      thumbTimer: null,
      draft: { builtin: 'classic', style: {} },
      colorFields: COLOR_FIELDS,
      printModes: PRINT_MODES
    };
  },
  computed: {
    dirty() {
      return JSON.stringify(this.draft) !== JSON.stringify(this.saved);
    },
    activePrintHint() {
      const m = PRINT_MODES.find((x) => x.id === this.draft.style.printMode);
      return m ? m.hint : '';
    }
  },
  watch: {
    draft: {
      deep: true,
      handler() {
        this.justSaved = false;
        // The parent drives the big preview from this, so changes are visible
        // before anything is written.
        this.$emit('change', { builtin: this.draft.builtin, style: this.draft.style });
      }
    },
    'draft.style.bodySize'() { this.refreshThumbs(); },
    'draft.style.printMode'() { this.refreshThumbs(); },
    'draft.style.accent'() { this.refreshThumbs(); },
    'draft.style.paper'() { this.refreshThumbs(); }
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load() {
      this.loadingTemplates = true;
      try {
        const [list, current] = await Promise.all([
          sparkforgeApi.listCardTemplates(),
          sparkforgeApi.getCardStyle(this.gameId)
        ]);
        this.templates = list.templates;
        const style = { ...list.styleTokens, ...(current.style || {}) };
        style.typeColors = style.typeColors || {};
        this.draft = { builtin: current.template.builtin || 'classic', style };
        this.saved = JSON.parse(JSON.stringify(this.draft));
        if (current.template.custom) {
          this.error =
            'This deck uses a custom uploaded template. Choosing a built-in layout below will replace it.';
        }
        this.refreshThumbs();
      } catch (err) {
        this.error = err.message;
      } finally {
        this.loadingTemplates = false;
      }
    },

    async refreshThumbs() {
      clearTimeout(this.thumbTimer);
      this.thumbTimer = setTimeout(async () => {
        const card = Object.keys(this.sampleCard || {}).length
          ? this.sampleCard
          : {
              name: 'Storm Lantern',
              type: 'Action',
              description: 'Draw 2. If the lamp is unlit, draw 3 instead.',
              flavorText: 'It burned all night, and so did she.',
              repeats: 3
            };
        // Sequential rather than parallel: six renders at once is a burst of
        // cold-start latency for no benefit.
        for (const tpl of this.templates) {
          try {
            const result = await sparkforgeApi.previewCard(this.gameId, card, {
              builtin: tpl.id,
              style: this.draft.style
            });
            this.thumbs = { ...this.thumbs, [tpl.id]: svgToDataUri(result.svg) };
          } catch (e) {
            /* a failed thumbnail is not worth surfacing */
          }
        }
      }, 250);
    },

    setTypeColor(type, value) {
      this.draft.style.typeColors = { ...this.draft.style.typeColors, [type]: value };
    },
    clearTypeColor(type) {
      const next = { ...this.draft.style.typeColors };
      delete next[type];
      this.draft.style.typeColors = next;
    },

    reset() {
      this.draft = JSON.parse(JSON.stringify(this.saved));
      this.refreshThumbs();
    },

    async save() {
      this.saving = true;
      this.error = null;
      try {
        if (this.draft.builtin !== this.saved.builtin) {
          await sparkforgeApi.setCardTemplate(this.gameId, { builtin: this.draft.builtin });
        }
        const { style } = await sparkforgeApi.setCardStyle(this.gameId, this.draft.style);
        this.draft.style = { ...style, typeColors: style.typeColors || {} };
        this.saved = JSON.parse(JSON.stringify(this.draft));
        this.justSaved = true;
        this.$emit('saved', this.draft);
      } catch (err) {
        this.error = err.message;
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.design-heading {
  font-family: var(--font-heading, inherit);
  margin-bottom: 0.15rem;
}
.design-hint {
  font-size: 0.8rem;
  color: var(--ink-light, #6b6b6b);
  margin-bottom: 0.75rem;
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 0.75rem;
}

.template-tile {
  background: var(--bg-surface, #fff);
  border: 2px solid var(--border-sketch, #ded8cd);
  border-radius: 12px;
  padding: 0.5rem;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease;
  text-align: center;
}
.template-tile:hover { transform: translateY(-2px); }
.template-tile.selected {
  border-color: var(--pop-orange, #e8590c);
  box-shadow: 0 0 0 3px rgba(232, 89, 12, 0.12);
}

.template-thumb {
  aspect-ratio: 180 / 252;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 6px;
}
.template-thumb.landscape { aspect-ratio: 252 / 180; }
.template-thumb img { width: 100%; height: 100%; object-fit: contain; }

.thumb-loading {
  width: 100%;
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #f2efe9 25%, #e9e5dd 37%, #f2efe9 63%);
  background-size: 400% 100%;
  animation: shimmer 1.3s ease-in-out infinite;
}
@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

.template-name {
  font-weight: 600;
  font-size: 0.82rem;
  margin-top: 0.35rem;
  text-transform: capitalize;
}
.template-orientation { font-size: 0.68rem; color: var(--ink-light, #8a8a8a); }

.value-chip {
  font-size: 0.72rem;
  min-width: 40px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--ink-light, #6b6b6b);
}

.color-row { display: flex; align-items: center; gap: 0.4rem; }
.form-control-color { width: 38px; padding: 2px; }
</style>
